/**
 * Pricing page.
 *
 * Three tiers: Free, Family (a consumer plan capped at five profiles) and
 * Organisation (schools, clubs and care settings). Organisation is deliberately
 * a "contact us" tier rather than a self-serve one — the profile allowance is
 * negotiated and provisioned per account, which keeps the capped consumer plan
 * from being used to provision a whole cohort. See `functions/src/shared/config.js`.
 *
 * Family is billed monthly (£3) or annually (£30 — two months free), chosen with
 * the cadence toggle. The Family call to action starts a hosted Stripe Checkout
 * for a signed-in, verified account, and otherwise routes to registration. No
 * Stripe code runs in the browser: the server returns a URL and the billing
 * service redirects to it. Entitlements never come from returning here (§13).
 */

import { el, icon } from "../ui/dom.js";
import { errorAlert, pageHeader } from "../ui/patterns.js";
import { supportHref } from "../config/support.js";

/** Family price by cadence. Placeholders until Stripe test prices are wired. */
const FAMILY_PRICING = {
  monthly: { price: "£3", cadence: "per month" },
  annual: { price: "£30", cadence: "per year" },
};

/**
 * @param {import("../app/router.js").RouteContext} context
 * @returns {Node}
 */
export default function renderPricing(context) {
  /** @type {"monthly" | "annual"} */
  let cadence = "monthly";

  const frag = document.createDocumentFragment();
  frag.append(
    pageHeader("Pricing", "Prices shown are placeholders while KinCode is in development."),
  );

  // The Family price nodes are updated in place when the cadence toggle changes.
  const priceStrong = el("strong", {}, [FAMILY_PRICING.monthly.price]);
  const priceCadence = el("span", { class: "kin-note" }, [` ${FAMILY_PRICING.monthly.cadence}`]);
  const saving = el("span", { class: "kin-plan-saving", hidden: true }, ["Two months free"]);

  function applyCadence() {
    const chosen = FAMILY_PRICING[cadence];
    priceStrong.textContent = chosen.price;
    priceCadence.textContent = ` ${chosen.cadence}`;
    saving.hidden = cadence !== "annual";
  }

  const toggle = el(
    "wa-switch",
    {
      class: "kin-cadence-toggle",
      on: {
        change: (/** @type {Event} */ event) => {
          cadence = /** @type {{ checked?: boolean }} */ (event.target).checked
            ? "annual"
            : "monthly";
          applyCadence();
        },
      },
    },
    ["Pay annually and get two months free"],
  );
  frag.append(el("div", { class: "kin-cadence" }, [toggle]));

  // An assertive region for a checkout error (e.g. an account that already
  // subscribes). Populated with the shared error alert, which conveys the problem
  // by icon and heading, not colour alone.
  const errorRegion = el("div", { "aria-live": "assertive" });

  const familyFeatures = [
    "Up to five recovery profiles",
    "Up to five contacts per profile",
    "Printable recovery cards",
    "Priority support",
  ];

  const grid = el("div", { class: "kin-plan-grid" }, [
    planCard({
      name: "Free",
      priceNodes: [el("strong", {}, ["£0"])],
      features: ["One recovery profile", "Up to three contacts", "Phrase and PIN recovery"],
      footer: internalCta("Create an account", "/register"),
    }),
    planCard({
      name: "Family",
      featured: true,
      priceNodes: [priceStrong, priceCadence, saving],
      features: familyFeatures,
      contactNote: {
        prompt: "Need more than five profiles?",
        label: "Contact us",
        subject: "KinCode — more than five profiles",
      },
      footer: el(
        "wa-button",
        {
          slot: "footer",
          variant: "brand",
          appearance: "filled",
          on: { click: () => startFamilyCheckout(context, () => cadence, errorRegion) },
        },
        ["Subscribe"],
      ),
    }),
    planCard({
      name: "Organisation",
      priceNodes: [el("strong", {}, ["Custom"])],
      features: [
        "For schools, clubs and care settings",
        "A provisioned number of profiles",
        "Central billing and priority support",
      ],
      footer: el(
        "wa-button",
        {
          slot: "footer",
          variant: "neutral",
          appearance: "outlined",
          href: supportHref("KinCode — Organisation enquiry"),
        },
        ["Contact us"],
      ),
    }),
  ]);

  frag.append(errorRegion, grid);
  return frag;
}

/**
 * Start a hosted Checkout for the Family plan, or route to registration first.
 *
 * The server requires a verified account, so an unauthenticated or unverified
 * visitor is sent to the right place rather than shown a failure.
 *
 * @param {import("../app/router.js").RouteContext} context
 * @param {() => "monthly" | "annual"} getCadence
 * @param {HTMLElement} errorRegion
 */
async function startFamilyCheckout(context, getCadence, errorRegion) {
  const account = context.services.auth.getCurrentAccount();
  if (!account) {
    context.navigate("/register");
    return;
  }
  if (!account.emailVerified) {
    context.navigate("/verify-email");
    return;
  }
  errorRegion.replaceChildren();
  try {
    await context.services.billing.startCheckout(getCadence());
    // On success the browser is redirected to Stripe; nothing more happens here.
  } catch (error) {
    errorRegion.replaceChildren(
      errorAlert(
        error instanceof Error ? error.message : "Could not start checkout. Please try again.",
      ),
    );
  }
}

/**
 * Build an internal-navigation call-to-action button.
 *
 * @param {string} label
 * @param {string} href
 * @returns {HTMLElement}
 */
function internalCta(label, href) {
  return el(
    "wa-button",
    { slot: "footer", variant: "neutral", appearance: "outlined", href, dataset: { link: "" } },
    [label],
  );
}

/**
 * Render one plan card.
 *
 * @param {{
 *   name: string,
 *   priceNodes: (Node | string)[],
 *   features: string[],
 *   footer: Node,
 *   featured?: boolean,
 *   contactNote?: { prompt: string, label: string, subject: string },
 * }} plan
 * @returns {HTMLElement}
 */
function planCard(plan) {
  return el("wa-card", { class: plan.featured ? "kin-plan kin-plan-featured" : "kin-plan" }, [
    el("div", { slot: "header", class: "kin-plan-head" }, [el("span", {}, [plan.name])]),
    el("p", { class: "kin-plan-price" }, plan.priceNodes),
    el(
      "ul",
      { class: "kin-plan-features" },
      plan.features.map((feature) =>
        el("li", {}, [icon("circle-check"), el("span", {}, [feature])]),
      ),
    ),
    plan.contactNote
      ? el("p", { class: "kin-plan-contact kin-note" }, [
          `${plan.contactNote.prompt} `,
          el("a", { href: supportHref(plan.contactNote.subject) }, [plan.contactNote.label]),
          ".",
        ])
      : null,
    plan.footer,
  ]);
}
